import React, { useState } from "react";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_FASTAPI_BASE_URL || process.env.REACT_APP_API_BASE_URL;

const WarningBanner = () => (
  <div className="bg-yellow-50/90 border border-yellow-300 rounded-xl p-4">
    <div className="flex justify-center">
      <div className="flex-shrink-0">
        <svg
          className="h-5 w-5 text-yellow-400"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>
      </div>
      <div className="ml-3">
        <p className="text-sm text-yellow-700 text-center">
          <strong>Disclaimer:</strong> The responses provided are AI-generated
          and may not be 100% accurate. For legal matters, always consult with a
          qualified legal professional.
        </p>
      </div>
    </div>
  </div>
);

const QueryPage = () => {
  const [query, setQuery] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleInputChange = (e) => {
    setQuery(e.target.value);
  };

  const handleSendMessage = async () => {
    if (!query.trim()) {
      setError("Please enter a valid query.");
      return;
    }

    setLoading(true);
    setError("");
    const userMessage = { type: "user", content: query };
    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setQuery("");

    try {
      let response;
      try {
        response = await axios.post(`${API_BASE_URL}/chatbot`, {
          user_message: query,
        });
      } catch (primaryError) {
        response = await axios.post(`${API_BASE_URL}/api/chatbot`, {
          user_message: query,
        });
      }
      const botMessage = {
        type: "bot",
        content: {
          lawyer_response: response.data.lawyer_response,
          potential_charges: response.data.potential_charges,
          rights: response.data.rights,
          next_steps: response.data.next_steps,
        },
      };
      setMessages((prevMessages) => [...prevMessages, botMessage]);
    } catch (err) {
      const status = err?.response?.status;
      setError(
        status
          ? `Failed to fetch results (HTTP ${status}).`
          : "Failed to fetch results. Please check backend URL/CORS."
      );
    }
    setLoading(false);
  };

  return (
    <div className="h-screen p-6 flex flex-col gap-4">
      <div className="bg-gradient-to-b from-white/70 via-white/40 to-white/30 backdrop-blur-xl border border-white/40 shadow-[0_10px_25px_rgba(0,0,0,0.08)] rounded-3xl px-6 py-4">
        <h1 className="text-2xl md:text-3xl font-semibold text-gray-800">Query Bot</h1>
        <p className="text-gray-600 mt-1">Ask legal questions and get structured AI guidance in conversational format.</p>
      </div>
      <div className="bg-gradient-to-b from-white/70 via-white/40 to-white/30 backdrop-blur-xl border border-white/40 shadow-[0_10px_25px_rgba(0,0,0,0.08)] rounded-3xl p-4 flex flex-col flex-1 min-h-0">
        <div className="p-2 pb-3 border-b border-gray-200">
          <WarningBanner />
        </div>
        <div className="flex-grow overflow-y-auto p-4">
          <div className="space-y-4">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${
                message.type === "user" ? "justify-end" : "justify-start"
              }`}
            >
              <div
                className={`max-w-xs md:max-w-md rounded-lg p-3 ${
                  message.type === "user"
                    ? "bg-gradient-to-r from-orange-400 to-orange-600 text-white"
                    : "bg-white/90 shadow-sm border border-gray-200"
                }`}
              >
                {message.type === "user" ? (
                  <p>{message.content}</p>
                ) : (
                  <div className="space-y-2">
                    <p>{message.content.lawyer_response}</p>
                    {message.content.potential_charges.length > 0 && (
                      <div>
                        <h4 className="font-semibold">Potential Charges:</h4>
                        <ul className="list-disc pl-5">
                          {message.content.potential_charges.map(
                            (charge, i) => (
                              <li key={i}>{charge}</li>
                            )
                          )}
                        </ul>
                      </div>
                    )}
                    {message.content.rights.length > 0 && (
                      <div>
                        <h4 className="font-semibold">Your Rights:</h4>
                        <ul className="list-disc pl-5">
                          {message.content.rights.map((right, i) => (
                            <li key={i}>{right}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {message.content.next_steps.length > 0 && (
                      <div>
                        <h4 className="font-semibold">Next Steps:</h4>
                        <ul className="list-disc pl-5">
                          {message.content.next_steps.map((step, i) => (
                            <li key={i}>{step}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
        <div className="bg-white/70 p-4 border-t border-gray-200 rounded-b-3xl">
        <div className="flex gap-2">
          <input
            type="text"
            className="flex-grow px-4 py-2 rounded-xl border border-gray-300 bg-white/80 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
            placeholder="Enter your query"
            value={query}
            onChange={handleInputChange}
          />
          <button
            className="bg-gradient-to-r from-orange-400 to-orange-600 text-white px-6 py-2 rounded-xl font-medium hover:from-orange-500 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-[#FF8A65] focus:ring-opacity-50"
            onClick={handleSendMessage}
            disabled={loading}
          >
            {loading ? "Sending..." : "Send"}
          </button>
        </div>
        {error && <p className="text-red-500 mt-2">{error}</p>}
      </div>
      </div>
    </div>
  );
};

export default QueryPage;
